/**
 * Road-sign arrow glyphs.
 *
 * Drawn in the style of motorway signage: thick stroke, rounded joins, high
 * contrast, everything in a 0 0 64 64 box pointing up (= straight ahead). The
 * same geometry is reused at two sizes - the large maneuver sign and the
 * per-lane chips - so the visual language stays consistent.
 *
 * Two things were wrong before and are fixed here:
 *
 *  1. The U-turn curled the wrong way. Thailand drives on the LEFT, so a
 *     U-turn swings out to the RIGHT (you cross the oncoming carriageway the
 *     way a right turn does). The old glyph showed the right-hand-traffic
 *     convention and pointed drivers into the wrong swing. Glyphs that depend
 *     on driving side now come in mirrored pairs, chosen from the step's own
 *     `driving_side`.
 *
 *  2. Forks, ramps and merges drew only the branch you take, which read as a
 *     plain slight turn. They now draw the road you're leaving as a dimmed
 *     "ghost" stroke, which is how the real signs read.
 */

import type { LaneIndication } from '../core/types';

interface Glyph {
  /** Painted at full strength: the path you take. */
  d: string;
  /** Painted dimmed: the branch you don't take, or the circle of a roundabout. */
  ghost?: string;
}

/**
 * Heads are two-armed chevrons whose apex sits at the end of the shaft, with
 * the arms swept back ~40 degrees either side of the direction of travel.
 */
const G: Record<string, Glyph> = {
  straight: { d: 'M32 56 V20 M20 31 L32 17 L44 31' },

  right: { d: 'M20 56 V40 C20 33 25 32 32 32 L44 32 M34 21 L47 32 L34 43' },
  left: { d: 'M44 56 V40 C44 33 39 32 32 32 L20 32 M30 21 L17 32 L30 43' },

  slightRight: { d: 'M24 56 V44 C24 34 30 26 41 20 M31 17 L44 18 L41 31' },
  slightLeft: { d: 'M40 56 V44 C40 34 34 26 23 20 M33 17 L20 18 L23 31' },

  /* Sharp turns double back on themselves, so the head points down and out. */
  sharpRight: { d: 'M24 56 V34 C24 25 33 21 40 26 L45 30 M47 19 L50 34 L35 34' },
  sharpLeft: { d: 'M40 56 V34 C40 25 31 21 24 26 L19 30 M17 19 L14 34 L29 34' },

  /* U-turn for LEFT-hand traffic (Thailand): the swing goes out to the right,
     so the shaft rises on the left and the arrow returns on the right. */
  uturn: { d: 'M23 54 V33 C23 21 41 21 41 33 V42 M34 39 L41 50 L48 39' },
  /* U-turn for right-hand traffic: mirrored. */
  uturnMirror: { d: 'M41 54 V33 C41 21 23 21 23 33 V42 M30 39 L23 50 L16 39' },

  /* Merging: your lane bends in, the road you join runs on past. */
  mergeRight: {
    d: 'M22 56 C22 42 30 38 32 30 M32 32 V18 M21 29 L32 15 L43 29',
    ghost: 'M46 56 C46 42 38 38 34 31',
  },
  mergeLeft: {
    d: 'M42 56 C42 42 34 38 32 30 M32 32 V18 M21 29 L32 15 L43 29',
    ghost: 'M18 56 C18 42 26 38 30 31',
  },

  forkRight: {
    d: 'M28 56 V42 C28 33 33 27 42 21 M32 18 L45 19 L42 32',
    ghost: 'M28 42 C28 33 24 28 17 23',
  },
  forkLeft: {
    d: 'M36 56 V42 C36 33 31 27 22 21 M32 18 L19 19 L22 32',
    ghost: 'M36 42 C36 33 40 28 47 23',
  },

  /* Ramps: the mainline carries straight on, you peel off. */
  rampRight: {
    d: 'M26 56 V40 C26 30 32 25 42 20 M32 17 L45 18 L42 31',
    ghost: 'M26 56 V16',
  },
  rampLeft: {
    d: 'M38 56 V40 C38 30 32 25 22 20 M32 17 L19 18 L22 31',
    ghost: 'M38 56 V16',
  },

  /* Roundabouts: the circle is the ghost, the solid stroke is your path in
     and out. Which exit is drawn comes from the maneuver modifier, because a
     generic "circle with a stub" tells the driver nothing about where to leave. */
  roundabout: {
    d: 'M32 58 V45 M32 15 V6 M25 13 L32 3 L39 13',
    ghost: 'M32 29 m -14 0 a 14 14 0 1 0 28 0 a 14 14 0 1 0 -28 0',
  },
  roundaboutRight: {
    d: 'M32 58 V45 M47 29 H57 M50 21 L60 29 L50 37',
    ghost: 'M32 29 m -14 0 a 14 14 0 1 0 28 0 a 14 14 0 1 0 -28 0',
  },
  roundaboutLeft: {
    d: 'M32 58 V45 M17 29 H7 M14 21 L4 29 L14 37',
    ghost: 'M32 29 m -14 0 a 14 14 0 1 0 28 0 a 14 14 0 1 0 -28 0',
  },

  /* Destination: a marker flag rather than another arrow. */
  arrive: { d: 'M20 58 V10 M20 12 H46 L40 22 L46 32 H20' },

  none: { d: 'M32 56 V20 M20 31 L32 17 L44 31' },
};

export type GlyphKey = keyof typeof G;
export type DrivingSide = 'left' | 'right';

/** Map Mapbox maneuver (type, modifier) to a glyph, honouring driving side. */
export function glyphForManeuver(
  type: string,
  modifier?: string,
  drivingSide: DrivingSide = 'left',
): GlyphKey {
  const m = (modifier ?? '').toLowerCase();
  const t = (type ?? '').toLowerCase();
  const leftDrive = drivingSide === 'left';

  switch (t) {
    case 'arrive':
      return 'arrive';
    case 'roundabout':
    case 'rotary':
    case 'roundabout turn':
    case 'exit roundabout':
    case 'exit rotary':
      // Show where to LEAVE the roundabout, not just that there is one.
      if (m.includes('left')) return 'roundaboutLeft';
      if (m.includes('right')) return 'roundaboutRight';
      return 'roundabout';
    case 'merge':
      return m.includes('left') ? 'mergeLeft' : 'mergeRight';
    case 'on ramp':
      return m.includes('left') ? 'rampLeft' : 'rampRight';
    case 'off ramp':
      // Unmarked exits leave on the side traffic keeps to.
      if (m.includes('right')) return 'rampRight';
      if (m.includes('left')) return 'rampLeft';
      return leftDrive ? 'rampLeft' : 'rampRight';
    case 'fork':
      return m.includes('left') ? 'forkLeft' : 'forkRight';
  }

  switch (m) {
    case 'uturn':
      // The whole point of the fix: swing right where traffic keeps left.
      return leftDrive ? 'uturn' : 'uturnMirror';
    case 'sharp right':
      return 'sharpRight';
    case 'sharp left':
      return 'sharpLeft';
    case 'right':
      return 'right';
    case 'left':
      return 'left';
    case 'slight right':
      return 'slightRight';
    case 'slight left':
      return 'slightLeft';
    default:
      return 'straight';
  }
}

export function glyphForLane(
  indication: LaneIndication,
  drivingSide: DrivingSide = 'left',
): GlyphKey {
  switch (indication) {
    case 'left':
      return 'left';
    case 'right':
      return 'right';
    case 'slight left':
      return 'slightLeft';
    case 'slight right':
      return 'slightRight';
    case 'sharp left':
      return 'sharpLeft';
    case 'sharp right':
      return 'sharpRight';
    case 'uturn':
      return drivingSide === 'left' ? 'uturn' : 'uturnMirror';
    default:
      return 'straight';
  }
}

/** Render a glyph as an SVG string. */
export function arrowSvg(key: GlyphKey, cls = ''): string {
  const g = G[key] ?? G.straight;
  const ghost = g.ghost
    ? `<path d="${g.ghost}" opacity="0.32" stroke-width="6" />`
    : '';
  return `<svg class="${cls}" viewBox="0 0 64 64" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="7" stroke-linecap="round" stroke-linejoin="round">${ghost}<path d="${g.d}" /></svg>`;
}

/**
 * A lane chip: the arrows painted on that lane, with the direction that stays
 * on route emphasised and the rest dimmed - exactly how overhead lane signs
 * read.
 *
 * `fallbackIndication` is the maneuver's own modifier. Without it, a lane
 * whose `valid_indication` Mapbox omitted used to light up EVERY arrow it
 * carries, which told the driver a left-turn-only lane was fine for a right
 * turn. That is the failure this argument exists to prevent.
 */
export function laneChipSvg(
  indications: LaneIndication[],
  validIndication: LaneIndication | undefined,
  valid: boolean,
  drivingSide: DrivingSide = 'left',
  fallbackIndication?: LaneIndication,
): string {
  const list = indications.length ? indications : ['straight' as LaneIndication];
  const chosen = validIndication ?? fallbackIndication;

  const inner = list
    .map((ind) => {
      const isTheOne =
        valid && (chosen !== undefined ? ind === chosen : list.length === 1);
      return `<span class="lane-chip__glyph ${isTheOne ? 'is-valid' : ''}">${arrowSvg(
        glyphForLane(ind, drivingSide),
      )}</span>`;
    })
    .join('');

  return `<span class="lane-chip ${valid ? 'lane-chip--valid' : 'lane-chip--invalid'}">${inner}</span>`;
}
